import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { HistoricalPrescription } from '../types/prescription';

interface HistoricalPrescriptionsProps {
  prescriptions: HistoricalPrescription[];
  onUpdatePrescription: (prescription: HistoricalPrescription) => void;
}

export function HistoricalPrescriptions({ 
  prescriptions,
  onUpdatePrescription
}: HistoricalPrescriptionsProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Get current page items
  const currentPrescriptions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return prescriptions.slice(startIndex, endIndex);
  }, [prescriptions, currentPage]);

  const totalPages = Math.ceil(prescriptions.length / itemsPerPage);

  const handleActiveToggle = (prescription: HistoricalPrescription) => {
    onUpdatePrescription({
      ...prescription,
      active: !prescription.active
    });
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader className="border-b">
        <CardTitle>Previous Prescriptions</CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-4">
          {currentPrescriptions.map((prescription) => (
            <div 
              key={prescription.id} 
              className="flex items-start justify-between p-4 rounded-lg border"
            >
              <div className="flex items-start gap-4 w-full">
                <div className="flex items-center h-5 mt-1">
                  <Checkbox
                    checked={prescription.active}
                    onCheckedChange={() => handleActiveToggle(prescription)}
                  />
                </div>
                <div className="space-y-1 flex-1">
                  <p className="font-medium">
                    {prescription.medication} - {prescription.dosage}
                  </p>
                  <p className="text-sm text-gray-600">
                    {prescription.frequency} for {prescription.duration}
                  </p>
                  {prescription.notes && (
                    <p className="text-sm text-gray-500">{prescription.notes}</p>
                  )}
                  <p className="text-xs text-gray-400">
                    Prescribed: {new Date(prescription.prescribedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center">
                  <span className={`px-2 py-1 rounded text-xs ${
                    prescription.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {prescription.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
          {prescriptions.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              No previous prescriptions
            </p>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="py-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
